import { Box, Button, Flex, IconLoading, Text } from "@powerledger/ui-component-lib";
import { Field, Formik } from "formik";
import { ChangeEvent, useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";

import { multipleLinkRegistrySchema } from "@/app/lib/validation";
import {
  AccountTypeSelectCard,
  getIcon,
} from "@/app/pages/public/signup/account-type-selection/account-type-selection-card";
import { NonRegistryTypeCheckBox } from "@/app/pages/public/signup/account-type-selection/non-registry-type-checkbox";
import {
  RegistryFormValues,
  RegistryFormViewProps,
} from "@/app/pages/settings/registry-tab/linked-registries/linked-registries.types";
import { AccountType, RegistryCode } from "@/app/types/generated/graphql";
import { TraceXSupportedRegistries } from "@/app/types/okta.types";

import { AccountNumberInput } from "../../account-number-input";
import { UserMessage } from "../../user-message/user-message";
import ErcotDetailsSection from "./ercot-details-section";
import { getInputFieldLabel, guidanceText, registryLogoMap } from "./helpers";

export const RegistryFormView = ({
  showLegalDocumentModal,
  setShowLegalDocumentModal,
  file,
  registryOptions,
  linkedRegistriesData,
  loading,
  loadingTranslatedFile,
  validateAccountNumber,
  linkRegistry,
  singlePage = false,
  closeModal,
  setSelectedRegistry,
  selectedRegistry,
  showModal = true,
  isRetryState = false,
  retryRegistryInfo,
}: RegistryFormViewProps) => {
  const { t } = useTranslation();
  const finishLinking = useCallback(() => {
    closeModal();
  }, [closeModal]);

  // When the registry is in retry state, automatically select the registry being retried
  // so the user doesn’t need to select it again and can directly choose the account type and proceed with linking.
  useEffect(() => {
    if (isRetryState && retryRegistryInfo?.registryId) {
      const getRetryRegistry = registryOptions?.find((registry) => registry.id === retryRegistryInfo?.registryId);
      setSelectedRegistry?.(getRetryRegistry);
    }
  }, [isRetryState, retryRegistryInfo?.registryId, registryOptions, setSelectedRegistry]);

  const isSelectedRegistryMRETS = selectedRegistry?.code === RegistryCode.Mrets;

  const renderRegistryForm = useCallback(
    ({ handleSubmit, isSubmitting, setFieldValue, setStatus, isValid, status, values, setValues }) => {
      return (
        <>
          {(!selectedRegistry || singlePage) && (
            <>
              <Flex data-testid="registry-form-select">
                {loading ? (
                  <IconLoading />
                ) : (
                  <Flex sx={{ flexDirection: "column", gap: 3 }}>
                    {!showModal && (
                      <UserMessage
                        message="You can complete onboarding for multiple products one after another or add others later from the Dashboard."
                        sx={{
                          width: "fit-content",
                          bg: "rgba(255, 250, 235, .6)",
                          borderRadius: 8,
                          p: 2,
                          span: {
                            color: "textDarker",
                            fontWeight: 400,
                            fontFamily: "MintGroteskV08",
                          },
                        }}
                      />
                    )}
                    {/* // Exclude ERCOT when MRETS is in retry state, and vice versa */}
                    <Flex sx={{ gap: 2 }}>
                      {registryOptions
                        ?.filter((registry) => !isRetryState || registry.id === retryRegistryInfo?.registryId)
                        ?.map((ro) => {
                          const linkedRegistry = linkedRegistriesData?.account?.linkedRegistries?.find(
                            (registry) => registry.registryId === ro.id,
                          );
                          // Allow selection if the linked registry is in a retry state
                          // otherwise, disable already selected registries.
                          const isDisabled = isRetryState ? retryRegistryInfo?.registryId !== ro.id : !!linkedRegistry;
                          return (
                            <Button
                              variant="outlined"
                              key={ro?.id}
                              data-testid={ro.code.toLowerCase()}
                              sx={{
                                borderRadius: 4,
                                border:
                                  selectedRegistry === ro
                                    ? "2px solid var(--theme-ui-colors-primary-500)"
                                    : "1px solid var(--theme-ui-colors-textDark)",
                                "&:hover, &:focus": isDisabled
                                  ? {}
                                  : {
                                      background: "primary.25",
                                    },
                                width: 150,
                                height: 80,
                                backgroundColor: selectedRegistry === ro ? "primary.25" : "pageBackground",
                                position: "relative",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                padding: 0,
                                cursor: isDisabled ? "not-allowed" : "pointer",
                                opacity: isDisabled && 0.5,
                              }}
                              onClick={() => {
                                setSelectedRegistry?.(ro);
                                setValues({
                                  registryId: ro?.id,
                                  registryCode: ro?.code,
                                  type: null,
                                  nrmTermsAccepted: false,
                                  accountNumber: "",
                                });
                                setStatus(undefined);
                              }}
                              disabled={isDisabled}
                            >
                              <Flex sx={{ width: "100%", px: 3, alignItems: "center" }}>
                                {registryLogoMap[ro?.code || ""]}
                              </Flex>
                            </Button>
                          );
                        })}
                    </Flex>
                  </Flex>
                )}
              </Flex>

              {!showModal && !selectedRegistry && (
                <Flex sx={{ justifyContent: "flex-start", alignItems: "center", mt: 4 }}>
                  <Button variant="inverted" sx={{ mr: 2 }} onClick={closeModal} type="button">
                    {t("Cancel")}
                  </Button>
                </Flex>
              )}
            </>
          )}

          {selectedRegistry && (
            <Flex sx={{ mt: 3, gap: 3, flexDirection: "column" }}>
              <Flex sx={{ flexDirection: "column", gap: 3, fontFamily: "MintGroteskV08" }}>
                {isSelectedRegistryMRETS && (
                  <>
                    <Text>{t("Please select the account type:")}</Text>
                    <Flex
                      sx={{
                        gap: 2,
                      }}
                      data-testid="registry-account-type-select"
                    >
                      <AccountTypeSelectCard
                        text={t("Product Holding Account")}
                        getIcon={(selected) => getIcon(selected, AccountType.NonRegistry)}
                        selected={values.type === AccountType.NonRegistry}
                        onClick={() => {
                          setValues((prev: RegistryFormValues) => ({
                            ...prev,
                            type: AccountType.NonRegistry,
                            accountNumber: "",
                          }));
                          setStatus(undefined);
                        }}
                        testID={`${selectedRegistry.code.toLowerCase()}-non-registry`}
                      />
                      <AccountTypeSelectCard
                        text={t("Link Registry Account")}
                        getIcon={(selected) => getIcon(selected, AccountType.Registry)}
                        selected={values.type === AccountType.Registry}
                        onClick={() => {
                          setValues((prev: RegistryFormValues) => ({
                            ...prev,
                            type: AccountType.Registry,
                            nrmTermsAccepted: false,
                          }));
                        }}
                        testID={`${selectedRegistry.code.toLowerCase()}-registry`}
                      />
                    </Flex>
                  </>
                )}
                <Box
                  sx={{
                    borderWidth: "0.5px",
                    borderStyle: "solid",
                    borderColor: "textDarker",
                  }}
                />
                {/* Show guidance text if `type` is not null or `registryCode` is ERCOT */}
                {(values.type || !isSelectedRegistryMRETS) && (
                  <UserMessage
                    message={guidanceText({
                      registryCode: selectedRegistry?.code as TraceXSupportedRegistries,
                      accountType: values.type,
                    })}
                    showInfoIcon={false}
                    sx={{
                      bg: "rgba(255, 250, 235, .6)",
                      borderRadius: 8,
                      p: 2,
                      span: {
                        fontWeight: 400,
                        color: "textDarker",
                        whiteSpace: "pre-line",
                        lineHeight: 1.7,
                      },
                    }}
                  />
                )}
              </Flex>

              {(values.type === AccountType.Registry || !isSelectedRegistryMRETS) && (
                <>
                  {!isSelectedRegistryMRETS && <ErcotDetailsSection />}
                  <Flex
                    sx={{
                      flexDirection: "column",
                      gap: 1,
                      color: "textDarker",
                      fontFamily: "MintGroteskV08",
                      fontWeight: 500,
                    }}
                  >
                    <Text>{getInputFieldLabel(selectedRegistry.code as RegistryCode)}</Text>
                    <Field
                      name="accountNumber"
                      validate={(value: string) => validateAccountNumber(value, status, setStatus)}
                      registryCode={selectedRegistry?.code}
                      component={AccountNumberInput}
                    />
                  </Flex>
                </>
              )}

              {(values.type === AccountType.NonRegistry || !isSelectedRegistryMRETS) && (
                <NonRegistryTypeCheckBox
                  t={t}
                  setShowLegalDocumentModal={setShowLegalDocumentModal}
                  isChecked={values.nrmTermsAccepted}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => setFieldValue("nrmTermsAccepted", e?.target.checked)}
                  showLegalDocumentModal={showLegalDocumentModal}
                  loadingTranslatedFile={loadingTranslatedFile}
                  file={file}
                  registry={selectedRegistry.code}
                />
              )}

              <Flex
                sx={{
                  justifyContent: showModal ? "flex-end" : "flex-start",
                  alignItems: "center",
                }}
              >
                {!showModal && (
                  <Button
                    variant="inverted"
                    sx={{ mr: 2 }}
                    onClick={() => {
                      setSelectedRegistry?.(false);
                      closeModal();
                    }}
                    type="button"
                  >
                    {t("Cancel")}
                  </Button>
                )}
                {showModal && status?.error && (
                  <Text sx={{ color: "error.500", fontSize: 1, mr: 3 }}>{t(status?.error)}</Text>
                )}
                {status?.success && (
                  <Text sx={{ color: "success.50", fontSize: 1, mr: 3 }}>
                    {t("The link request has been submitted. You'll be notified when it has been confirmed.")}
                  </Text>
                )}
                {!status?.success && (
                  <Flex sx={{ alignItems: "center" }}>
                    <Button
                      type="submit"
                      variant="primary"
                      disabled={isSubmitting || !isValid}
                      data-testid={"registry-form--link"}
                      onClick={() => handleSubmit()}
                    >
                      {t("Proceed")}
                    </Button>
                    {isSubmitting && <IconLoading size="small" sx={{ ml: 2 }} />}
                  </Flex>
                )}
                {status?.success && (
                  <Button variant="primary" sx={{ px: 4 }} onClick={finishLinking}>
                    {isSubmitting ? <IconLoading /> : t("Finish")}
                  </Button>
                )}
                {!showModal && status?.error && (
                  <Text sx={{ color: "error.500", fontSize: 1, ml: 3 }}>{t(status?.error)}</Text>
                )}
              </Flex>
            </Flex>
          )}
        </>
      );
    },
    [
      closeModal,
      showModal,
      finishLinking,
      registryOptions,
      selectedRegistry,
      setSelectedRegistry,
      singlePage,
      t,
      validateAccountNumber,
      linkedRegistriesData,
      isRetryState,
      file,
      loadingTranslatedFile,
      showLegalDocumentModal,
      loading,
      setShowLegalDocumentModal,
      retryRegistryInfo?.registryId,
      isSelectedRegistryMRETS,
    ],
  );
  return (
    <Formik
      initialValues={{
        registryId: isRetryState ? retryRegistryInfo?.registryId || "" : "",
        type: null,
        accountNumber: "",
        nrmTermsAccepted: false,
        registryCode: isRetryState ? retryRegistryInfo?.registryCode : selectedRegistry?.code,
      }}
      validationSchema={multipleLinkRegistrySchema}
      onSubmit={async ({ registryId, accountNumber, type }, { setStatus }) => {
        const accountType = type ?? (accountNumber ? AccountType.Registry : AccountType.NonRegistry);
        try {
          await linkRegistry({
            variables: {
              registryLink: {
                registryCode: selectedRegistry?.code as RegistryCode,
                termsAndConditionsAccepted: true,
                accountType,
                registryId,
                registryOrgLinkId: accountNumber || undefined,
              },
            },
          });
          //remove selectedRegistry once linked
          setSelectedRegistry?.(false);
          setStatus({
            success: true,
            error: "",
          });
          closeModal();
        } catch (e: any) {
          setStatus({
            success: false,
            error: e?.graphQLErrors[0]?.message || "Something went wrong",
          });
        }
      }}
      validateOnMount
    >
      {(props) => renderRegistryForm(props)}
    </Formik>
  );
};
